/**
 * 网络请求封装
 * 自动处理 Token 认证、90天持久登录和错误处理
 */

// API 基础地址
// 生产环境应为空字符串 (使用相对路径，由Nginx反向代理)
// 开发环境如果是 H5 且配置了 proxy，也可以为空
// 如果是 App 或无代理开发环境，则指向实际后端地址
// API 基础地址
// 生产环境应为空字符串 (使用相对路径，由Nginx反向代理)
// 开发环境如果是 H5 且配置了 proxy，也可以为空
// 如果是 App 或无代理开发环境，则指向实际后端地址
let BASE_URL = ''

function shouldPreferRelativeApiUrl() {
  // #ifdef H5
  return process.env.NODE_ENV === 'development'
  // #endif

  // #ifndef H5
  return false
  // #endif
}

function resolveApiUrl(url, options = {}) {
  const { preferRelativeOnH5Dev = false } = options
  if (!url) return BASE_URL
  if (/^https?:\/\//i.test(url)) return url

  const normalizedUrl = url.startsWith('/') ? url : `/${url}`
  const isApiRequest = normalizedUrl === '/api' || normalizedUrl.startsWith('/api/')

  const shouldForceRelativeApiUrl = shouldPreferRelativeApiUrl() && (preferRelativeOnH5Dev || isApiRequest)
  if (shouldForceRelativeApiUrl) {
    return normalizedUrl
  }

  // 可选：若用户显式指定 api_base_url，则使用它（用于临时切到云端）
  const stored = uni.getStorageSync('api_base_url')
  const explicitApiBaseUrl = typeof stored === 'string' ? stored.trim().replace(/\/$/, '') : ''
  if (explicitApiBaseUrl) {
    return `${explicitApiBaseUrl}${normalizedUrl}`
  }

  return `${BASE_URL}${normalizedUrl}`
}

/** 将 FastAPI / 其它接口返回体中的错误信息整理为一句文案 */
function normalizeHttpErrorMessage(data) {
  if (data == null) return ''
  if (typeof data === 'string') {
    const t = data.trim()
    return t.length > 200 ? `${t.slice(0, 200)}…` : t
  }
  if (typeof data === 'object') {
    const d = data.detail
    if (typeof d === 'string' && d.trim()) return d.trim()
    if (Array.isArray(d) && d.length) {
      const parts = d
        .map((x) => {
          if (!x || typeof x !== 'object') return ''
          return (x.msg || x.message || '').toString().trim()
        })
        .filter(Boolean)
      if (parts.length) return parts.join('；')
    }
    const m = data.message || data.msg || data.error
    if (typeof m === 'string' && m.trim()) return m.trim()
  }
  return ''
}

/** 将各类二进制响应统一转为 ArrayBuffer */
function normalizeBinaryBuffer(data) {
  if (data instanceof ArrayBuffer) return data
  if (ArrayBuffer.isView(data)) {
    return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength)
  }
  if (data && typeof data === 'object' && Array.isArray(data.data)) {
    return Uint8Array.from(data.data).buffer
  }
  if (typeof data === 'string') {
    const bytes = new Uint8Array(data.length)
    for (let i = 0; i < data.length; i += 1) {
      bytes[i] = data.charCodeAt(i) & 0xff
    }
    return bytes.buffer
  }
  return null
}

function resolveBinaryApiUrl(url) {
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`
  const stored = uni.getStorageSync('api_base_url')
  const explicit = typeof stored === 'string' ? stored.trim().replace(/\/$/, '') : ''
  if (explicit) {
    return `${explicit}${normalizedUrl}`
  }

  try {
    if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
      const proxyTarget = String(import.meta.env.VITE_API_PROXY_TARGET || '').trim().replace(/\/$/, '')
      if (proxyTarget) {
        return `${proxyTarget}${normalizedUrl}`
      }
      return `http://127.0.0.1:8000${normalizedUrl}`
    }
  } catch (e) {}

  return resolveApiUrl(url)
}

async function decodeArrayBufferError(buffer) {
  if (!(buffer instanceof ArrayBuffer)) return '导出失败'
  try {
    const text = new TextDecoder('utf-8').decode(buffer.slice(0, Math.min(buffer.byteLength, 800)))
    const trimmed = text.trim()
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      const parsed = JSON.parse(trimmed)
      if (typeof parsed === 'string' && parsed.trim()) return parsed.trim()
      if (parsed && typeof parsed === 'object') {
        return parsed.detail || parsed.message || parsed.error || '导出失败'
      }
    }
  } catch (e) {}
  return '导出失败'
}

/**
 * 使用 XHR 下载二进制（比 uni.request / fetch 更稳定，避免 docx 损坏）
 */
function requestArrayBufferViaXHR(options) {
  const {
    url,
    method = 'GET',
    data = {},
    header = {},
    needAuth = true,
    showLoading = false,
    showNavLoading = false,
    showError = true,
    loadingText = '加载中...',
    timeout = 60000,
    preferDirectBackend = true,
  } = options

  if (showNavLoading) {
    uni.showNavigationBarLoading()
  } else if (showLoading) {
    uni.showLoading({ title: loadingText, mask: false })
  }

  const requestUrl = preferDirectBackend ? resolveBinaryApiUrl(url) : resolveApiUrl(url)

  return new Promise((resolve, reject) => {
    if (typeof XMLHttpRequest === 'undefined') {
      reject(new Error('当前环境不支持二进制下载'))
      return
    }

    const token = needAuth ? getToken() : ''
    if (needAuth && !token) {
      handleAuthFailure(reject, showError, '请先登录')
      return
    }

    const xhr = new XMLHttpRequest()
    xhr.open(method, requestUrl, true)
    xhr.responseType = 'arraybuffer'
    xhr.timeout = timeout

    const headers = { ...header }
    const hasContentType = Object.keys(headers).some(
      (key) => key.toLowerCase() === 'content-type',
    )
    if (!hasContentType && method !== 'GET' && method !== 'HEAD') {
      headers['Content-Type'] = 'application/json'
    }
    if (needAuth && token) {
      headers.Authorization = `Bearer ${token}`
    }
    Object.entries(headers).forEach(([key, value]) => {
      if (value != null) xhr.setRequestHeader(key, value)
    })

    xhr.onload = async () => {
      const buffer = normalizeBinaryBuffer(xhr.response)
      if (xhr.status >= 200 && xhr.status < 300) {
        if (!buffer) {
          reject(new Error('导出响应为空'))
          return
        }
        resolve(buffer)
        return
      }

      if (isAuthenticationError(xhr.status, null)) {
        handleAuthFailure(reject, showError)
        return
      }

      const errorMsg = buffer
        ? await decodeArrayBufferError(buffer)
        : `请求失败（HTTP ${xhr.status || 'ERR'}）`
      if (showError) {
        uni.showToast({ title: errorMsg, icon: 'none', duration: 2000 })
      }
      const error = new Error(errorMsg)
      error.statusCode = xhr.status
      reject(error)
    }

    xhr.onerror = () => {
      const errorMsg = '网络连接失败，请检查网络或后端服务是否已启动'
      if (showError) {
        uni.showToast({ title: errorMsg, icon: 'none', duration: 2000 })
      }
      reject(new Error(errorMsg))
    }

    xhr.ontimeout = () => {
      const errorMsg = '请求超时，请稍后再试'
      if (showError) {
        uni.showToast({ title: errorMsg, icon: 'none', duration: 2000 })
      }
      reject(new Error(errorMsg))
    }

    xhr.onabort = () => {
      reject(new Error('请求已取消'))
    }

    xhr.onloadend = () => {
      if (showNavLoading) {
        uni.hideNavigationBarLoading()
      } else if (showLoading) {
        uni.hideLoading()
      }
    }

    xhr.send(method !== 'GET' && method !== 'HEAD' ? JSON.stringify(data) : null)
  })
}

async function readFetchErrorMessage(response, fallback = 'Request failed') {
  if (!response) {
    return fallback
  }

  const statusFallback = response.status ? `HTTP ${response.status}` : fallback

  try {
    const raw = await response.text()
    const text = String(raw || '').trim()
    if (!text) {
      return statusFallback
    }

    try {
      const parsed = JSON.parse(text)
      if (typeof parsed === 'string' && parsed.trim()) {
        return parsed.trim()
      }
      if (parsed && typeof parsed === 'object') {
        return parsed.detail || parsed.message || parsed.error || statusFallback
      }
    } catch (e) {}

    if (/^<!doctype html/i.test(text) || /^<html/i.test(text)) {
      return statusFallback
    }

    return text.slice(0, 200)
  } catch (e) {
    return statusFallback
  }
}

let authRedirectTimer = null

function isLoginPage() {
  try {
    if (typeof getCurrentPages !== 'function') return false
    const pages = getCurrentPages()
    const currentPage = pages && pages[pages.length - 1]
    return currentPage && currentPage.route === 'pages/login/index'
  } catch (e) {
    return false
  }
}

function scheduleLoginRedirect() {
  if (authRedirectTimer) return
  authRedirectTimer = setTimeout(() => {
    authRedirectTimer = null
    if (isLoginPage()) return
    uni.reLaunch({
      url: '/pages/login/index'
    })
  }, 600)
}

function isAuthenticationError(statusCode, data) {
  if (statusCode === 401) return true
  if (statusCode !== 403) return false

  const errorMsg = normalizeHttpErrorMessage(data).toLowerCase()
  return errorMsg === 'not authenticated' || errorMsg === 'invalid authentication credentials'
}

function handleAuthFailure(reject, showError, message = '登录已过期，请重新登录') {
  const shouldNotify = !authRedirectTimer
  removeToken()
  if (showError && shouldNotify) {
    uni.showToast({
      title: message,
      icon: 'none',
      duration: 2000
    })
  }
  scheduleLoginRedirect()

  const error = new Error(message)
  error.statusCode = 401
  reject(error)
}

// #ifdef H5
if (process.env.NODE_ENV === 'development') {
   // H5 本地开发默认走本地代理（vite proxy /api -> 本机后端）
   BASE_URL = ''
}
// #endif

// #ifndef H5
// 非H5环境（如App/小程序）必须使用完整URL，因为无法使用相对路径代理
BASE_URL = 'https://xmark.club'
// BASE_URL = 'http://127.0.0.1:8001'
// #endif

console.log('🔗 API Base URL:', BASE_URL)

// Token 过期时间常量（90天，单位：毫秒）
const TOKEN_EXPIRE_DAYS = 90
const TOKEN_EXPIRE_MS = TOKEN_EXPIRE_DAYS * 24 * 60 * 60 * 1000

/**
 * 获取存储的 Token
 * 自动检查是否过期，过期则清除并返回空
 */
function getToken() {
  try {
    const tokenData = uni.getStorageSync('token_data')
    // 兼容旧版本只保存 access_token 的登录会话；否则会被当作未登录，
    // /config/global 将返回全部模型，导致管理员刚配置的模型权限看起来不生效。
    if (!tokenData) {
      return String(uni.getStorageSync('access_token') || '')
    }

    const { token, expireTime, remember } = tokenData

    // 检查是否过期
    if (remember && expireTime) {
      const now = Date.now()
      if (now > expireTime) {
        // Token 已过期，清除并返回空
        console.log('🔒 Token 已过期，需要重新登录')
        removeToken()
        return ''
      }
    }

    return token || String(uni.getStorageSync('access_token') || '')
  } catch (e) {
    console.error('获取 Token 失败:', e)
    return ''
  }
}

/**
 * 保存 Token
 * @param {string} token - 访问令牌
 * @param {boolean} remember - 是否记住登录（90天内保持登录）
 */
function setToken(token, remember = false) {
  try {
    const tokenData = {
      token: token,
      remember: remember,
      createdAt: Date.now()
    }

    // 登录态统一保留90天；除非用户主动退出或后端判定 Token 失效。
    tokenData.remember = true
    tokenData.expireTime = Date.now() + TOKEN_EXPIRE_MS
    console.log('🔐 已开启90天持久登录，过期时间:', new Date(tokenData.expireTime).toLocaleString())

    uni.setStorageSync('token_data', tokenData)

    // 同时保存到 access_token（兼容旧代码）
    uni.setStorageSync('access_token', token)
  } catch (e) {
    console.error('保存 Token 失败:', e)
  }
}

/**
 * 清除 Token
 */
function removeToken() {
  try {
    uni.removeStorageSync('token_data')
    uni.removeStorageSync('access_token')
    uni.removeStorageSync('userInfo')
    console.log('🗑️ 已清除登录凭证')
  } catch (e) {
    console.error('清除 Token 失败:', e)
  }
}

/**
 * 检查是否已登录且未过期
 * @returns {boolean} 是否处于有效登录状态
 */
function isLoggedIn() {
  const token = getToken()
  return !!token
}

/**
 * 获取 Token 剩余有效时间（天）
 * @returns {number} 剩余天数，-1 表示未登录或已过期
 */
function getTokenRemainingDays() {
  try {
    const tokenData = uni.getStorageSync('token_data')
    if (!tokenData || !tokenData.expireTime) return -1

    const remaining = tokenData.expireTime - Date.now()
    if (remaining <= 0) return -1

    return Math.ceil(remaining / (24 * 60 * 60 * 1000))
  } catch (e) {
    return -1
  }
}

/**
 * 验证 Token 是否有效（调用后端接口确认）
 * 网络异常（request:fail）时不视为 Token 无效，避免后端未启动时误清登录
 * @returns {Promise<boolean>} Token 是否有效
 */
async function validateToken() {
  const token = getToken()
  if (!token) return false

  try {
    const res = await new Promise((resolve, reject) => {
      uni.request({
        url: resolveApiUrl('/api/v1/user/me'),
        method: 'GET',
        header: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        success: (res) => {
          if (res.statusCode >= 200 && res.statusCode < 300) {
            resolve(res.data)
          } else if (res.statusCode === 401) {
            reject(new Error('UNAUTHORIZED'))
          } else {
            reject(new Error('Token 无效'))
          }
        },
        fail: (err) => reject(err)
      })
    })
    console.log('✅ Token 验证成功')
    return true
  } catch (e) {
    const isNetworkFail = (e && (e.errMsg === 'request:fail' || (e.message && e.message.includes('fail'))))
    if (isNetworkFail) {
      console.warn('⚠️ 无法连接服务器（请确认后端已启动 ' + BASE_URL + '），保留本地登录状态')
      return true
    }
    if (e && e.message === 'UNAUTHORIZED') {
      console.log('❌ Token 已失效，需重新登录')
      return false
    }
    console.log('❌ Token 验证失败:', e.message || e)
    return false
  }
}

/**
 * 统一的请求方法
 * @param {Object} options 请求配置
 * @returns {Promise}
 */
function request(options) {
  const { responseType } = options

  if (responseType === 'arraybuffer' && typeof XMLHttpRequest !== 'undefined') {
    return requestArrayBufferViaXHR(options)
  }

  return new Promise((resolve, reject) => {
    const {
      url,
      method = 'GET',
      data = {},
      header = {},
      needAuth = true, // 是否需要认证
      showLoading = false, // 是否显示加载提示（默认关闭，需要时手动开启）
      showNavLoading = false, // 使用导航栏加载标识（比全屏遮罩更优雅）
      showError = true, // 是否显示错误提示
      loadingText = '加载中...',
      timeout = 60000 // 默认60秒超时，AI生成请求可传入更长时间
    } = options

    // 显示加载提示（优先使用导航栏加载，更优雅）
    if (showNavLoading) {
      uni.showNavigationBarLoading()
    } else if (showLoading) {
      uni.showLoading({
        title: loadingText,
        mask: false // 默认不锁死屏幕，允许用户操作
      })
    }

    // 构建请求头
    const requestHeader = {
      'Content-Type': 'application/json',
      ...header
    }

    // 如果需要认证，自动添加 Token
    if (needAuth) {
      const token = getToken()
      if (token) {
        requestHeader['Authorization'] = `Bearer ${token}`
      } else {
        if (showNavLoading) {
          uni.hideNavigationBarLoading()
        } else if (showLoading) {
          uni.hideLoading()
        }
        handleAuthFailure(reject, showError, '请先登录')
        return
      }
    }

    // 发起请求
    uni.request({
      url: resolveApiUrl(url),
      method: method,
      data: data,
      header: requestHeader,
      timeout: timeout, // 添加超时配置
      responseType: responseType || 'text',
      success: (res) => {
        // 隐藏加载提示
        if (showNavLoading) {
          uni.hideNavigationBarLoading()
        } else if (showLoading) {
          uni.hideLoading()
        }

        // HTTP 状态码检查
        if (res.statusCode >= 200 && res.statusCode < 300) {
          // 请求成功
          if (responseType === 'arraybuffer') {
            const buffer = normalizeBinaryBuffer(res.data)
            if (buffer) {
              resolve(buffer)
              return
            }
            reject(new Error('导出响应格式异常'))
            return
          }
          resolve(res.data)
        } else if (isAuthenticationError(res.statusCode, res.data)) {
          handleAuthFailure(reject, showError)
        } else {
          // 其他错误（含 FastAPI detail 为字符串或校验错误数组）
          const errorMsg =
            normalizeHttpErrorMessage(res.data) ||
            (typeof res.data === 'string' && res.data.trim() ? res.data.trim().slice(0, 200) : '') ||
            `请求失败（HTTP ${res.statusCode || 'ERR'}）`
          if (showError) {
            uni.showToast({
              title: errorMsg,
              icon: 'none',
              duration: 2000
            })
          }
          // 将完整的错误信息传递给 reject
          const error = new Error(errorMsg)
          error.statusCode = res.statusCode
          error.data = res.data
          reject(error)
        }
      },
      fail: (err) => {
        // 隐藏加载提示
        if (showNavLoading) {
          uni.hideNavigationBarLoading()
        } else if (showLoading) {
          uni.hideLoading()
        }

        // 网络错误处理
        let errorMsg = '网络请求失败，请检查网络连接'
        if (err.errMsg) {
          if (err.errMsg.includes('timeout')) {
            errorMsg = '请求超时，请稍后再试'
          } else if (err.errMsg.includes('fail')) {
            errorMsg = '网络连接失败，请检查网络设置'
          }
        }

        if (showError) {
          uni.showToast({
            title: errorMsg,
            icon: 'none',
            duration: 2000
          })
        }
        const networkError = new Error(errorMsg)
        networkError.errMsg = err && err.errMsg
        networkError.original = err
        reject(networkError)
      }
    })
  })
}

/**
 * GET 请求
 */
function get(url, data = {}, options = {}) {
  return request({
    url,
    method: 'GET',
    data,
    ...options
  })
}

/**
 * POST 请求
 */
function post(url, data = {}, options = {}) {
  return request({
    url,
    method: 'POST',
    data,
    ...options
  })
}

/**
 * PUT 请求
 */
function put(url, data = {}, options = {}) {
  return request({
    url,
    method: 'PUT',
    data,
    ...options
  })
}

/**
 * DELETE 请求
 */
function del(url, data = {}, options = {}) {
  return request({
    url,
    method: 'DELETE',
    data,
    ...options
  })
}

/**
 * PATCH 请求
 */
function patch(url, data = {}, options = {}) {
  return request({
    url,
    method: 'PATCH',
    data,
    ...options
  })
}

// 导出
export default {
  request,
  get,
  post,
  put,
  patch,
  delete: del,
  getToken,
  setToken,
  removeToken,
  isLoggedIn,
  getTokenRemainingDays,
  validateToken,
  resolveApiUrl,
  readFetchErrorMessage,
  BASE_URL,
  TOKEN_EXPIRE_DAYS
}
